/** Battle setup: armies in, a populated struct-of-arrays `BattleState` out.
 *
 * Everything here happens once, so it is allowed to allocate. After this file
 * the kernel never allocates a stack again except for summons.
 */

import type { ArmyId, CreatureDefId, HeroId, PlayerId, SpellDefId } from "../ids.js";
import { asCreatureDefId, asModifierSrcId } from "../ids.js";
import type { CreatureModifiers, Modifier } from "../modifiers.js";
import { FP_ONE, clampInt } from "../numeric.js";
import { type RngStreamState, seedStream } from "../rng/pcg32.js";
import { RNG_STREAMS } from "../rng/streams.js";
import { type ContentRegistry, type CreatureDef, type MagicGroundDef, must } from "../content/registry.js";
import type { ArmySlot } from "../state.js";
import {
  BF_HEIGHT, BF_WIDTH, F_TWO_HEX, F_UNDEAD, F_WAR_MACHINE,
  SIDE_ATTACKER, SIDE_DEFENDER,
  type BattleHeroState, type BattleState, type Obstacle, type StackStore,
  hexIndex,
} from "./state.js";
import {
  ARTILLERY_DAMAGE_FP, STAT_CREW_ARTILLERY, STAT_CREW_BALLISTICS, STAT_CREW_FIRST_AID,
  defaultSiegeEngine, machineRoleOf, sideHasBreaker,
} from "./machines.js";
import {
  castsForCreature, flagsForCreature, shotsForCreature, strikesForCreature,
} from "./creature.js";
import { computeRoundOrder } from "./order.js";
import { STAT_DAMAGE_MELEE, STAT_DAMAGE_RANGED, STAT_DAMAGE_TAKEN } from "./damage.js";
import { heroMagicModifiers } from "./spells.js";
import { groundCreatureModifiers, groundModifiers } from "./ground.js";
import { startCountModifier } from "./revenge.js";
import { FIELD_OBSTACLE_STREAM } from "./field.js";
import { generateWet, wetKindFromTerrain, type WetKind } from "./wet.js";
import { inputSideFaction, paintPerkHexes, startModsForStack } from "./armyPerk.js";

// ------------------------------------------------------------------- inputs

export interface BattleHeroInput {
  readonly id: HeroId | null;
  readonly owner: PlayerId | null;
  readonly attack: number;
  readonly defense: number;
  readonly spellPower: number;
  readonly knowledge: number;
  readonly mana: number;
  readonly spellbook: readonly SpellDefId[];
  readonly army: ArmyId | null;
  /** Leadership + artifacts + terrain, already summed by the adventure kernel. */
  readonly morale?: number;
  readonly luck?: number;
  /** Offense / Archery / Armorer, fixed point: 100 means 10%. */
  readonly offenseFp?: number;
  readonly archeryFp?: number;
  readonly armorerFp?: number;
  /** Flat damage multipliers from artifacts, fixed point, added to the bracket. */
  readonly meleeDamageFp?: number;
  readonly rangedDamageFp?: number;
  /**
   * The magic profile, carried the same way Offense and Armorer are: the battle
   * kernel has no `HeroState` and therefore no skills, so anything the spell
   * chain reads has to arrive as a Modifier on the army and be read back out of
   * state, where it hashes, journals and replays.
   */
  readonly wisdom?: number;
  /** School mastery 0..3 per school; `all` is derived as the best of the four. */
  readonly mastery?: Partial<Record<"air" | "earth" | "fire" | "water", number>>;
  /** Spellcraft and everything else that scales spell damage, fixed point. */
  readonly sorceryFp?: number;
  /** Artifacts and towns that make a cast cheaper or longer-lasting. */
  readonly spellCostDelta?: number;
  readonly spellDurationBonus?: number;
  /**
   * War machines this hero fields, as creature defs - Section 27. They come
   * from the artifacts in his `warMachine*` slots, so the adventure kernel
   * names them and the battle kernel only has to stand them on a hex. The
   * besieging hero's siege engine is added here by `startBattle` itself,
   * because the classic does not let him leave the catapult at home.
   */
  readonly machines?: readonly CreatureDefId[];
  /**
   * Ballistics / Artillery / First Aid, 0..3. They crew the machines: a machine
   * whose owner has the skill is commanded by the player, and an untrained one
   * is resolved by the kernel (Section 27's uncrewed rule).
   */
  readonly ballistics?: number;
  readonly artillery?: number;
  readonly firstAid?: number;
  /**
   * Bonuses that belong to one creature def rather than to the whole army - a
   * hero's creature specialty, and nothing else so far. They are unpacked here
   * because this is the only place that knows which stack is which; downstream
   * they are ordinary modifiers on ordinary stacks.
   */
  readonly creatureModifiers?: readonly CreatureModifiers[];
  /** Spell-specialty multipliers, keyed by spell id, fixed point. */
  readonly spellBonusFp?: Readonly<Record<string, number>>;
  /** School-scoped spell damage, keyed by school, fixed point - the four orbs. */
  readonly schoolDamageFp?: Readonly<Record<string, number>>;
  /**
   * Modifiers the adventure kernel wants on every stack of this army: an
   * artifact's magic resistance, its immunity to one named spell, the distance
   * penalty a bow cancels. They are ordinary modifiers, and this field exists
   * so that adding one is a data edit rather than a new input field.
   */
  readonly armyModifiers?: readonly Modifier[];
  /**
   * Modifiers that belong to the *field* rather than to a side: a Recanter's
   * Cloak silences both heroes, and Chains of Battle hold both of them. They are
   * installed on every stack on both sides, so whichever hero is asking gets
   * the same answer.
   */
  readonly fieldModifiers?: readonly Modifier[];
  /** Morale and luck this hero takes off the OTHER side. */
  readonly moraleEnemy?: number;
  readonly luckEnemy?: number;
  /**
   * Warding: the fixed-point share of the OTHER hero's spell power this
   * one leaves standing. FP_ONE for a hero without the skill.
   */
  readonly spellPowerEnemyFp?: number;
}

export interface BattleSideInput {
  /** Seven slots, `null` for empty; slot order is the player's own ordering. */
  readonly slots: readonly (ArmySlot | null)[];
  readonly hero: BattleHeroInput | null;
}

export interface StartBattleInput {
  readonly attacker: BattleSideInput;
  readonly defender: BattleSideInput;
  readonly terrain: string;
  /**
   * Section 31 - the magic ground this battle is fought on, or absent.
   *
   * The def rather than an id, because `startBattle` is handed the content
   * registry anyway and a battle replayed from a journal must resolve the same
   * ground: an id the bundle no longer knows would silently become ordinary
   * ground, and the modifiers are already in the journal.
   */
  readonly ground?: MagicGroundDef | undefined;
  readonly siege: BattleState["siege"];
  readonly obstacles?: readonly Obstacle[];
  /**
   * Battle RNG. Either a root seed (streams are derived from it, so an isolated
   * replay and an in-campaign battle roll identically) or the adventure kernel's
   * already-advanced `battle.*` streams handed straight across.
   */
  readonly rng: number | Readonly<Record<string, RngStreamState>>;
  /**
   * The tactics band, signed: positive gives the attacker that many columns,
   * negative the defender, zero no phase. See `BattleState.tacticsHexes`.
   */
  readonly tacticsHexes?: number;
  /** Who soaks the board. Omitted means derive from `terrain` (usually dry). */
  readonly wetKind?: WetKind;
}

// -------------------------------------------------------------- placement

/**
 * Vertical spread of k stacks over the eleven battlefield rows, exactly as the classic
 * lays them out: one stack centres, and each additional stack expands outward
 * symmetrically at a two-row pitch until seven stacks fill the column.
 * Index is the stack count; entry is the row for each slot, top to bottom.
 */
export const START_ROWS: readonly (readonly number[])[] = [
  [],
  [5],
  [3, 7],
  [1, 5, 9],
  [1, 3, 7, 9],
  [1, 3, 5, 7, 9],
  [0, 2, 4, 6, 8, 10],
  [0, 1, 3, 5, 7, 9, 10],
];

export const ATTACKER_COL = 0;
export const DEFENDER_COL = BF_WIDTH - 1;

/**
 * A two-hex stack occupies `hex` and `hex - facing`, so its head sits one column
 * inward from the army column: the tail is what lands on the outer column.
 */
/**
 * What fighting on your own faction's ground is worth (H5's rule, and the
 * reason a Deepvault player wants the battle underground).
 *
 * One and one. A larger number would make terrain decide battles rather than
 * colour them, and this kernel has no way for a player to choose the ground of
 * a fight he is defending - so a big native bonus would mostly be a tax on
 * whoever gets attacked at home, which is precisely backwards.
 */
export const NATIVE_TERRAIN_SPEED = 1;
export const NATIVE_TERRAIN_MORALE = 1;

export function startHex(side: 0 | 1, row: number, twoHex: boolean): number {
  if (side === SIDE_ATTACKER) return hexIndex(twoHex ? ATTACKER_COL + 1 : ATTACKER_COL, row);
  return hexIndex(twoHex ? DEFENDER_COL - 1 : DEFENDER_COL, row);
}

/**
 * Rows for the war-machine park, outermost first: 0, 10, 1, 9, 2, 8 ... A
 * machine takes the first free hex of its side's back column and spills into
 * the next column only when seven stacks have filled the first, so it stands
 * behind the line rather than in front of it and never displaces a creature.
 */
const MACHINE_ROWS: readonly number[] = Object.freeze(
  Array.from({ length: BF_HEIGHT }, (_, k) => ((k & 1) === 0 ? k >> 1 : BF_HEIGHT - 1 - (k >> 1))),
);

function machineHex(side: 0 | 1, taken: readonly number[]): number {
  for (let depth = 0; depth < 2; depth++) {
    const col = side === SIDE_ATTACKER ? ATTACKER_COL + depth : DEFENDER_COL - depth;
    for (const row of MACHINE_ROWS) {
      const h = hexIndex(col, row);
      if (!taken.includes(h)) return h;
    }
  }
  return -1;
}

/**
 * What a side actually wheels onto the field: the hero's own machines, plus the
 * siege engine every besieging hero brings whether he wants to or not. A side
 * with no hero brings nothing - a town garrison has no catapult of its own.
 */
function sideMachines(
  input: BattleSideInput, side: 0 | 1, siege: boolean, content: ContentRegistry,
  hasWet: boolean,
): readonly CreatureDefId[] {
  const hero = input.hero;
  if (hero === null) return [];
  const scow = asCreatureDefId("warmachine.scow");
  const tent = asCreatureDefId("warmachine.field_hospital");
  const out: CreatureDefId[] = [];
  for (const id of hero.machines ?? []) {
    // A Scow is a boat-ballista: on a dry field it is a Field Hospital instead,
    // which is the rule that keeps Blackwater honest inland.
    out.push(!hasWet && id === scow ? tent : id);
  }
  if (side === SIDE_ATTACKER && siege) {
    const engine = defaultSiegeEngine(content);
    if (engine !== null && !out.includes(engine)) out.push(engine);
  }
  return out;
}

// ---------------------------------------------------------------- abilities
//
// Minted in `creature.ts` and re-exported here, where every caller already
// looks for them. They had to move: `summon.ts` needs them to stand a summoned
// stack up, and `spells.ts` needs `summon.ts` - so a declaration in this file
// closed init -> spells -> summon -> init, and an ESM cycle is a
// `ReferenceError` at the first battle rather than a lint warning.

export {
  castsForCreature, flagsForCreature, shotsForCreature, strikesForCreature,
} from "./creature.js";

// ------------------------------------------------------------------ morale

/**
 * Section 24: what matters is the number of distinct alignments, not the number
 * of stacks. One faction is a +1 bonus, two is neutral, and each further faction
 * is another -1. Undead never take morale at all, and their presence costs the
 * living stacks in the same army one point.
 */
export function armyMoraleModifier(factions: readonly string[], anyUndead: boolean, anyLiving: boolean): number {
  const distinct: string[] = [];
  for (const f of factions) if (!distinct.includes(f)) distinct.push(f);
  const k = distinct.length;
  let m = k <= 1 ? 1 : 2 - k;
  if (anyUndead && anyLiving) m -= 1;
  return m;
}

// -------------------------------------------------------------------- start

interface Built {
  readonly def: CreatureDefId[];
  readonly side: number[];
  readonly slotInArmy: number[];
  readonly count: number[];
  readonly maxHp: number[];
  readonly hex: number[];
  readonly attack: number[];
  readonly defense: number[];
  readonly speed: number[];
  readonly damageMin: number[];
  readonly damageMax: number[];
  readonly shots: number[];
  readonly casts: number[];
  readonly strikes: number[];
  readonly flags: number[];
  readonly facing: number[];
  readonly mods: (readonly Modifier[])[];
}

function emptyBuilt(): Built {
  return {
    def: [], side: [], slotInArmy: [], count: [], maxHp: [], hex: [], attack: [],
    defense: [], speed: [], damageMin: [], damageMax: [], shots: [], casts: [], strikes: [],
    flags: [], facing: [], mods: [],
  };
}

const SRC_OFFENSE = asModifierSrcId("skill.offense");
const SRC_ARCHERY = asModifierSrcId("skill.archery");
const SRC_ARMORER = asModifierSrcId("skill.armorer");
const SRC_ART_MELEE = asModifierSrcId("artifact.melee_damage");
const SRC_ART_RANGED = asModifierSrcId("artifact.ranged_damage");

/**
 * The hero's Offense, Archery, Armorer and damage artifacts become Modifiers on
 * every stack in the army at battle start, so the damage formula reads them out
 * of state like any other number (D9) instead of taking them as an argument.
 */
export function heroCombatModifiers(h: BattleHeroInput | null): readonly Modifier[] {
  if (h === null) return [];
  const out: Modifier[] = [];
  const add = (source: typeof SRC_OFFENSE, stat: typeof STAT_DAMAGE_MELEE, value: number): void => {
    if (value === 0) return;
    out.push({ source, stat, op: "add", value, expires: { kind: "battle" } });
  };
  add(SRC_OFFENSE, STAT_DAMAGE_MELEE, h.offenseFp ?? 0);
  add(SRC_ARCHERY, STAT_DAMAGE_RANGED, h.archeryFp ?? 0);
  add(SRC_ARMORER, STAT_DAMAGE_TAKEN, h.armorerFp ?? 0);
  add(SRC_ART_MELEE, STAT_DAMAGE_MELEE, h.meleeDamageFp ?? 0);
  add(SRC_ART_RANGED, STAT_DAMAGE_RANGED, h.rangedDamageFp ?? 0);
  out.push(...heroMagicModifiers({
    wisdom: h.wisdom ?? 0,
    mastery: h.mastery ?? {},
    sorceryFp: h.sorceryFp ?? FP_ONE,
    spellCostDelta: h.spellCostDelta ?? 0,
    spellDurationBonus: h.spellDurationBonus ?? 0,
    spellBonusFp: h.spellBonusFp ?? {},
    schoolDamageFp: h.schoolDamageFp ?? {},
  }));
  for (const m of h.armyModifiers ?? []) out.push(m);
  return out;
}

/** The field's own modifiers: whatever either hero imposes on both armies. */
function fieldModifiers(input: StartBattleInput): readonly Modifier[] {
  const out: Modifier[] = [];
  for (const side of [input.attacker, input.defender]) {
    for (const m of side.hero?.fieldModifiers ?? []) out.push(m);
  }
  return out;
}

/**
 * The creature-scoped half of the hero's bonuses, for one def. Every stack in
 * the army is asked, so a hero specialised in a creature he happens to be
 * fielding twice gets it on both stacks - which is what the classic does.
 */
const SRC_CREW = asModifierSrcId("skill.machine_crew");
const SRC_CREW_DAMAGE = asModifierSrcId("skill.artillery.damage");

/**
 * What the hero's machine skills put on one machine's own stack: the crew level
 * for its role, and - for a gun - the damage the training is worth. Only the
 * matching role is written, so a hero with Ballistics does not find himself
 * commanding the tent.
 */
function machineCrewMods(h: BattleHeroInput | null, def: CreatureDef): readonly Modifier[] {
  if (h === null) return [];
  const role = machineRoleOf(def);
  const level = clampInt(
    role === "breaker" ? h.ballistics ?? 0
      : role === "artillery" ? h.artillery ?? 0
        : role === "medic" ? h.firstAid ?? 0 : 0,
    0, 3);
  if (level <= 0) return [];
  const out: Modifier[] = [{
    source: SRC_CREW,
    stat: role === "breaker" ? STAT_CREW_BALLISTICS
      : role === "artillery" ? STAT_CREW_ARTILLERY : STAT_CREW_FIRST_AID,
    op: "max", value: level, expires: { kind: "battle" },
  }];
  if (role === "artillery") {
    const mul = ARTILLERY_DAMAGE_FP[level] ?? FP_ONE;
    if (mul !== FP_ONE) {
      out.push({ source: SRC_CREW_DAMAGE, stat: "damageMin", op: "mulFp", value: mul, expires: { kind: "battle" } });
      out.push({ source: SRC_CREW_DAMAGE, stat: "damageMax", op: "mulFp", value: mul, expires: { kind: "battle" } });
    }
  }
  return out;
}

function creatureModsFor(
  h: BattleHeroInput | null, def: CreatureDefId,
): readonly Modifier[] {
  if (h === null || h.creatureModifiers === undefined) return [];
  const out: Modifier[] = [];
  for (const cm of h.creatureModifiers) if (cm.def === def) out.push(...cm.mods);
  return out;
}

function pushSide(
  b: Built, input: BattleSideInput, side: 0 | 1, content: ContentRegistry,
  machines: readonly CreatureDefId[],
): void {
  const present: ArmySlot[] = [];
  const slotIdx: number[] = [];
  for (let i = 0; i < input.slots.length; i++) {
    const s = input.slots[i];
    if (s === null || s === undefined || s.count <= 0) continue;
    present.push(s);
    slotIdx.push(i);
  }
  const rows = START_ROWS[clampInt(present.length, 0, 7)]!;
  const hero = input.hero;
  const heroAttack = hero ? hero.attack : 0;
  const heroDefense = hero ? hero.defense : 0;
  const heroMods = heroCombatModifiers(hero);

  for (let k = 0; k < present.length; k++) {
    const slot = present[k]!;
    const def = must(content.creatures, slot.def, "creature");
    const flags = flagsForCreature(def);
    const twoHex = (flags & F_TWO_HEX) !== 0;
    b.def.push(def.id);
    b.side.push(side);
    b.slotInArmy.push(slotIdx[k]!);
    b.count.push(slot.count);
    b.maxHp.push(def.hp);
    b.hex.push(startHex(side, rows[k]!, twoHex));
    b.attack.push(def.attack + heroAttack);
    b.defense.push(def.defense + heroDefense);
    b.speed.push(def.speed);
    b.damageMin.push(def.damageMin);
    b.damageMax.push(def.damageMax);
    b.shots.push(shotsForCreature(def));
    b.casts.push(castsForCreature(def));
    b.strikes.push(strikesForCreature(def));
    b.flags.push(flags);
    b.facing.push(side === SIDE_ATTACKER ? 1 : -1);
    b.mods.push([...heroMods, ...creatureModsFor(hero, def.id)]);
  }

  // Machines are parked after the army so they never take a creature's hex, and
  // they carry `slotInArmy` -1: they are equipment, and the battle-end fold
  // writes casualties back by slot index, so a machine must not name one.
  const taken: number[] = [];
  for (let i = 0; i < b.hex.length; i++) {
    if (b.side[i] !== side) continue;
    taken.push(b.hex[i]!);
    if ((b.flags[i]! & F_TWO_HEX) !== 0) taken.push(b.hex[i]! - b.facing[i]!);
  }
  const fielded: CreatureDefId[] = [];
  for (const id of machines) {
    if (fielded.includes(id)) continue;
    // Content that does not ship the machine simply fields none of it; a
    // missing war machine is a thinner army, never a failed battle.
    const def = content.creatures.get(id);
    if (def === undefined) continue;
    const hex = machineHex(side, taken);
    if (hex < 0) break;
    fielded.push(id);
    taken.push(hex);
    b.def.push(def.id);
    b.side.push(side);
    b.slotInArmy.push(-1);
    b.count.push(1);
    b.maxHp.push(def.hp);
    b.hex.push(hex);
    b.attack.push(def.attack + heroAttack);
    b.defense.push(def.defense + heroDefense);
    b.speed.push(def.speed);
    b.damageMin.push(def.damageMin);
    b.damageMax.push(def.damageMax);
    b.shots.push(shotsForCreature(def));
    b.casts.push(castsForCreature(def));
    b.strikes.push(strikesForCreature(def));
    b.flags.push(flagsForCreature(def));
    b.facing.push(side === SIDE_ATTACKER ? 1 : -1);
    b.mods.push([...heroMods, ...creatureModsFor(hero, def.id), ...machineCrewMods(hero, def)]);
  }
}

/**
 * Section 5 - Warding. The other hero's skill is felt as a share taken off
 * this hero's spell power, floored at one: a magic hero interfered with casts
 * weaker spells, never no spells at all.
 */
function heroState(h: BattleHeroInput | null, foe: BattleHeroInput | null): BattleHeroState {
  if (h === null) {
    return {
      id: null, owner: null, attack: 0, defense: 0, spellPower: 0, knowledge: 0,
      mana: 0, spellbook: [], castThisRound: false, racialUsed: false, wounds: 0, army: null,
    };
  }
  const share = foe?.spellPowerEnemyFp ?? FP_ONE;
  const power = share === FP_ONE || h.spellPower <= 0
    ? h.spellPower
    : Math.max(1, Math.floor((h.spellPower * share) / FP_ONE));
  return {
    id: h.id, owner: h.owner, attack: h.attack, defense: h.defense,
    spellPower: power, knowledge: h.knowledge, mana: h.mana,
    spellbook: [...h.spellbook], castThisRound: false, racialUsed: false, wounds: 0, army: h.army,
  };
}

export function defaultBattleRng(rootSeed: number): Record<string, RngStreamState> {
  const out: Record<string, RngStreamState> = {};
  for (const s of RNG_STREAMS) if (s.startsWith("battle.")) out[s] = seedStream(rootSeed, s);
  return out;
}

export function startBattle(input: StartBattleInput, content: ContentRegistry): BattleState {
  const rng: Record<string, RngStreamState> =
    typeof input.rng === "number" ? defaultBattleRng(input.rng) : { ...input.rng };
  for (const s of RNG_STREAMS) {
    if (!s.startsWith("battle.")) continue;
    if (rng[s] === undefined) rng[s] = seedStream(0, s);
  }

  const kind = input.wetKind ?? wetKindFromTerrain(input.terrain);
  let wet: Uint8Array | undefined;
  if (kind !== "none") {
    const seed = rng[FIELD_OBSTACLE_STREAM];
    if (seed !== undefined) {
      const [mask, next] = generateWet(kind, seed);
      rng[FIELD_OBSTACLE_STREAM] = next;
      let any = false;
      for (let i = 0; i < mask.length; i++) if (mask[i] !== 0) { any = true; break; }
      if (any) wet = mask;
    }
  }
  let fire: Uint8Array | undefined;
  let poison: Uint8Array | undefined;
  {
    const seed = rng[FIELD_OBSTACLE_STREAM];
    if (seed !== undefined) {
      const painted = paintPerkHexes(
        input.terrain, input.attacker.slots, input.defender.slots, content, seed,
      );
      rng[FIELD_OBSTACLE_STREAM] = painted.rng;
      fire = painted.fire;
      poison = painted.poison;
    }
  }
  const hasWet = wet !== undefined;

  const b = emptyBuilt();
  const siegeActive = input.siege !== null && input.siege.active;
  pushSide(b, input.attacker, SIDE_ATTACKER, content,
    sideMachines(input.attacker, SIDE_ATTACKER, siegeActive, content, hasWet));
  pushSide(b, input.defender, SIDE_DEFENDER, content,
    sideMachines(input.defender, SIDE_DEFENDER, siegeActive, content, hasWet));
  const n = b.def.length;

  // A Oathbreaker's Cloak silences the man wearing it too, so the field's own
  // modifiers go on every stack rather than on the side that brought them.
  const field = fieldModifiers(input);
  if (field.length > 0) {
    for (let i = 0; i < n; i++) b.mods[i] = [...b.mods[i]!, ...field];
  }

  // The ground, on the same terms and for the same reason: what a place does,
  // it does to everyone standing in it. `groundCreatureModifiers` is the half
  // that reads the creature - Blessed Earth lifting the living and weighing on
  // the dead - and it is asked per stack because that is the only thing about a
  // stack this rule looks at.
  const ground = input.ground;
  if (ground !== undefined) {
    const all = groundModifiers(ground);
    for (let i = 0; i < n; i++) {
      const mine = groundCreatureModifiers(content, ground, b.def[i] as unknown as string);
      if (all.length > 0 || mine.length > 0) b.mods[i] = [...b.mods[i]!, ...all, ...mine];
    }
  }

  const morale = new Int32Array(n);
  const luck = new Int32Array(n);
  for (const side of [SIDE_ATTACKER, SIDE_DEFENDER] as const) {
    const hero = side === SIDE_ATTACKER ? input.attacker.hero : input.defender.hero;
    const factions: string[] = [];
    let anyUndead = false, anyLiving = false;
    for (let i = 0; i < n; i++) {
      if (b.side[i] !== side) continue;
      // A war machine has no alignment and no morale: it must not count as a
      // second faction, or wheeling out the catapult would cost the army a
      // point of morale for the crime of being equipment.
      if ((b.flags[i]! & F_WAR_MACHINE) !== 0) continue;
      const def = must(content.creatures, b.def[i]!, "creature");
      if ((b.flags[i]! & F_UNDEAD) !== 0) anyUndead = true;
      else { anyLiving = true; factions.push(def.faction as string); }
    }
    const armyMorale = armyMoraleModifier(factions, anyUndead, anyLiving);
    // A Amulet of Ruin is worn by the other hero and felt here.
    const foe = side === SIDE_ATTACKER ? input.defender.hero : input.attacker.hero;
    const baseMorale = (hero?.morale ?? 0) + armyMorale - (foe?.moraleEnemy ?? 0);
    const baseLuck = (hero?.luck ?? 0) - (foe?.luckEnemy ?? 0);
    for (let i = 0; i < n; i++) {
      if (b.side[i] !== side) continue;
      const numb = (b.flags[i]! & (F_UNDEAD | F_WAR_MACHINE)) !== 0;
      morale[i] = numb ? 0 : clampInt(baseMorale, -3, 3);
      luck[i] = clampInt(baseLuck, -3, 3);
    }
  }

  // Auras are creature-driven, so they are applied after both sides exist.
  for (let i = 0; i < n; i++) {
    const def = must(content.creatures, b.def[i]!, "creature");
    for (const a of def.abilities) {
      switch (a.kind) {
        case "MORALE_AURA":
          for (let k = 0; k < n; k++) if (b.side[k] === b.side[i]) morale[k] = clampInt(morale[k]! + a.delta, -3, 3);
          break;
        case "LUCK_AURA":
          for (let k = 0; k < n; k++) if (b.side[k] === b.side[i]) luck[k] = clampInt(luck[k]! + a.delta, -3, 3);
          break;
        // The Pebbleshot's own good fortune, which it does not share.
        case "SELF_LUCK":
          luck[i] = clampInt(luck[i]! + a.delta, -3, 3);
          break;
        case "REDUCE_ENEMY_MORALE":
          for (let k = 0; k < n; k++) if (b.side[k] !== b.side[i]) morale[k] = clampInt(morale[k]! - a.delta, -3, 3);
          break;
        case "REDUCE_ENEMY_LUCK":
          for (let k = 0; k < n; k++) if (b.side[k] !== b.side[i]) luck[k] = clampInt(luck[k]! - a.delta, -3, 3);
          break;
        default: break;
      }
    }
  }
  for (let i = 0; i < n; i++) if ((b.flags[i]! & F_UNDEAD) !== 0) morale[i] = 0;

  // The count each stack STARTED with, and this is the one moment that number
  // exists. Revenge reads it, and so does every resurrection, as the ceiling a
  // raised stack may not grow past. Written into a modifier rather than a new
  // `StackStore` column, so the save format does not move.
  for (let i = 0; i < n; i++) b.mods[i] = [...(b.mods[i] ?? []), startCountModifier(b.count[i]!)];

  /**
   * Home ground: a creature fighting on its own faction's terrain is a step
   * quicker and a point braver.
   *
   * Both halves of this have existed since the factions were authored and never
   * met. `FactionDef.nativeTerrain` has always said which ground a faction
   * belongs to - `nativeTerrainOf` reads it on the adventure map, where it pays
   * a movement bonus - and `StartBattleInput.terrain` has always carried the
   * tile the fight is happening on, where it was used for nothing but choosing
   * which obstacles to scatter. Terrain was scenery in a battle.
   *
   * It is applied per STACK rather than per side, because an army is not
   * required to be of one faction: a Castle hero leading two Greenmantle stacks
   * across grass has one of those things at home and the other two abroad, and
   * saying so is both more correct and more interesting than a side-wide test.
   *
   * A war machine has no faction to be at home in and is skipped, for the same
   * reason it is skipped by the morale penalty above. The undead line below is
   * already past, so an undead stack keeps its zero morale and takes only the
   * speed - which is right: a skeleton is not cheered up by the scenery.
   *
   * One point each, deliberately small. It is a tilt, not a trump: a Deepvault
   * army does not become a different army underground, it moves first.
   */
  {
    const here = input.terrain;
    for (let i = 0; i < n; i++) {
      if ((b.flags[i]! & F_WAR_MACHINE) !== 0) continue;
      const def = must(content.creatures, b.def[i]!, "creature");
      const faction = content.factions.get(def.faction);
      if (faction === undefined || (faction.nativeTerrain as string) !== here) continue;
      b.speed[i] = b.speed[i]! + NATIVE_TERRAIN_SPEED;
      if ((b.flags[i]! & F_UNDEAD) === 0) {
        morale[i] = clampInt(morale[i]! + NATIVE_TERRAIN_MORALE, -3, 3);
      }
    }
  }

  const atkPerk = inputSideFaction(content, input.attacker.slots);
  const defPerk = inputSideFaction(content, input.defender.slots);
  for (let i = 0; i < n; i++) {
    const perk = b.side[i] === SIDE_ATTACKER ? atkPerk : defPerk;
    const extra = startModsForStack(b.flags[i]!, b.damageMax[i]!, perk);
    if (extra.length > 0) b.mods[i] = [...(b.mods[i] ?? []), ...extra];
  }

  const topHp = new Int32Array(n);
  for (let i = 0; i < n; i++) topHp[i] = b.maxHp[i]!;

  const stacks: StackStore = {
    n,
    def: b.def,
    side: Int32Array.from(b.side),
    slotInArmy: Int32Array.from(b.slotInArmy),
    count: Int32Array.from(b.count),
    topHp,
    maxHp: Int32Array.from(b.maxHp),
    hex: Int32Array.from(b.hex),
    attack: Int32Array.from(b.attack),
    defense: Int32Array.from(b.defense),
    speed: Int32Array.from(b.speed),
    damageMin: Int32Array.from(b.damageMin),
    damageMax: Int32Array.from(b.damageMax),
    shots: Int32Array.from(b.shots),
    casts: Int32Array.from(b.casts),
    strikes: Int32Array.from(b.strikes),
    morale,
    luck,
    flags: Int32Array.from(b.flags),
    flags2: new Int32Array(n),
    cooldown: new Int32Array(n),
    facing: Int32Array.from(b.facing),
    mods: b.mods,
    killed: new Int32Array(n),
  };

  /**
   * The gate, and the reason it can be barred at all.
   *
   * A besieged gate stays shut only while somebody on the field can open it.
   * If the attacker brings no breaker the bridge is already down when the
   * battle opens, because masonry no stack can cross is not a hard siege but a
   * battle that cannot be concluded. When he does bring one the gate is barred
   * and stays barred until the engine batters it: a breaker cannot be shot at
   * (Section 27), so a gate barred in round one is a gate that comes down.
   */
  const gateOpen = input.siege === null ? false
    : !siegeActive ? input.siege.gateOpen
      : input.siege.gateOpen || !sideHasBreaker(stacks, SIDE_ATTACKER);
  const siege = input.siege === null ? null : { ...input.siege, gateOpen };

  const state: BattleState = {
    round: 1,
    turnIndex: 0,
    order: new Int32Array(n),
    stacks,
    heroes: [
      heroState(input.attacker.hero, input.defender.hero),
      heroState(input.defender.hero, input.attacker.hero),
    ],
    obstacles: input.obstacles === undefined ? [] : [...input.obstacles],
    terrain: input.terrain,
    siege,
    rng,
    outcome: { kind: "ongoing" },
    waited: new Int32Array(n),
    tacticsPhase: (input.tacticsHexes ?? 0) !== 0,
    tacticsHexes: input.tacticsHexes ?? 0,
    ...(wet !== undefined ? { wet } : {}),
    ...(fire !== undefined ? { fire } : {}),
    ...(poison !== undefined ? { poison } : {}),
  };
  computeRoundOrder(state, state.order);
  return state;
}
